import {
  getBudgetedRoles as getBudgetedRolesFromProjectHelpers,
  getProjectTotalAllocation as getProjectTotalAllocationFromProjectHelpers,
} from "~/helpers/project-helpers"
import { getDefaultRate } from "~/helpers/rate-helpers"

import { BudgetedRole, ProjectRate, RateCard } from "./types"

// If someone we get something like 100.50 -> 100.99 we round to 100 instead of 101. Otherwise round normally
export const roundBudget = (number) =>
  number % 10 < 1 ? Math.floor(number) : Math.round(number)

export const calculateBudgetFromRoleMinutesAndRate = (
  roleMinutes: number,
  roleRate: number,
): number =>
  roleMinutes ? roundBudget((roleMinutes / 60) * Number(roleRate)) : 0

/**
 * Rails/Hasura -> Node migration helpers
 *
 * We've got ourselves into a bit of a pickle here where we use snake case for
 * Hasura in some legacy Rails Hasura actions, but all our Node mutations use
 * camel case. This means when we get data or use some shared calculations outside
 * of this project budget form, we may need to translate between the two.
 *
 * These are helpers for that translation.
 */
export const getBudgetedRoles = (
  ...args: Parameters<typeof getBudgetedRolesFromProjectHelpers>
) => {
  return getBudgetedRolesFromProjectHelpers(...args).map((role) => ({
    id: role.id,
    name: role.name,
    active: role.active,
    estimatedMinutes: role.estimated_minutes,
    projectRoleId: role.project_role_id,
    isScheduled: role.isScheduled,
    implicit: role.implicit,
  }))
}

export const getProjectTotalAllocation = (
  roles: ReadonlyArray<BudgetedRole>,
  projectRates: ReadonlyArray<ProjectRate>,
) => {
  return getProjectTotalAllocationFromProjectHelpers(
    roles.map((role) => ({
      id: role.id,
      estimated_minutes: role.estimatedMinutes,
      name: role.name,
      active: role.active,
      project_role_id: role.projectRoleId,
      isScheduled: role.isScheduled,
      implicit: role.implicit,
    })),
    projectRates.map((rate) => ({
      ...rate,
      role_id: rate.roleId,
    })),
  )
}

export const getEffectiveRate = (
  roleId: number,
  rateCard: RateCard,
  projectRates: ReadonlyArray<ProjectRate>,
): number => {
  const rateCardRate = getDefaultRate(rateCard, roleId)
  const projectRate = projectRates.find((rate) => rate.roleId === roleId)

  return projectRate?.rate ?? rateCardRate
}

export const calculatePhaseTotalMinutes = (
  phaseRoles: ReadonlyArray<{ estimatedMinutes: number }>,
): number => {
  return phaseRoles.reduce((sum, role) => {
    return sum + role.estimatedMinutes
  }, 0)
}

export const calculatePhaseTotalBudget = (
  phaseRoles: ReadonlyArray<{ roleId: number; estimatedMinutes: number }>,
  rateCard: RateCard,
  projectRates: ReadonlyArray<ProjectRate>,
): number => {
  return phaseRoles.reduce((sum, phaseRole) => {
    const effectiveRate = getEffectiveRate(
      phaseRole.roleId,
      rateCard,
      projectRates,
    )

    const roleBudget = calculateBudgetFromRoleMinutesAndRate(
      phaseRole.estimatedMinutes,
      effectiveRate,
    )

    return sum + roleBudget
  }, 0)
}

export const calculatePhaseTotalOtherExpenses = (
  phaseExpenses: ReadonlyArray<{ charge: number }>,
): number => {
  return phaseExpenses.reduce((sum, expense) => {
    return sum + expense.charge
  }, 0)
}
